import {
  TranscriptionsService,
  Transcription,
  DeleteTranscriptionResponse,
  DownloadUrlResponse
} from '@/api/';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useStore } from '@nanostores/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  Loader2,
  Trash,
  Download
} from 'lucide-react';
import { useEffect } from 'react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
/**
 * We'll update the selected workspace in the store and route
 * to the projects for this workspace.
 */
import { ReactNode } from 'react';
function sanitizeName(name: string): string {
  // Replace any invalid characters with underscores
  return name.replace(/[^a-z0-9_\-]/gi, '_');
}

function SortableItem({ id, children }: { id: string; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
}

export type TranscriptionListingProps = {
  organizationId: string;
};

export const TranscriptionListing = ({ organizationId }: TranscriptionListingProps) => {
  const org = useStore($org);
  const queryClient = useQueryClient();
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  );
  // Query to get the workspaces of the organization
  const transcriptions = useQuery<Transcription[]>({
    queryKey: ['getTranscriptions', organizationId],
    enabled: !!organizationId,
    queryFn: async () => {
      const res = await TranscriptionsService.getTranscriptions(organizationId);
      return res;
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });



  function constructOrderMap(orders: string[]): Map<string, number> {
    const orderMap = new Map<string, number>();
    orders.forEach((item, index) => {
      orderMap.set(item, index);
    });
    return orderMap;
  }

  function sortTranscriptionsUsingSavedOrders(
    items: Transcription[],
  ): Transcription[] {
    const savedTranscriptionOrders = JSON.parse(
      localStorage.getItem('transcriptions_orders') || '{}',
    );

    if (Object.keys(savedTranscriptionOrders)?.includes(organizationId)) {
      const transcriptionOrder = savedTranscriptionOrders[organizationId];

      // construct the order map
      const transcriptionOrderMap = constructOrderMap(transcriptionOrder);
      return items.sort((a, b) => {
        const orderA =
          transcriptionOrderMap.get(a?.id) ?? Number.MAX_SAFE_INTEGER;
        const orderB =
          transcriptionOrderMap.get(b?.id) ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });
    }

    return items;
  }

  // Load workspace order from local storage
  useEffect(() => {
    if (transcriptions?.isSuccess) {
      if (transcriptions?.data?.length > 0) {
        $transcriptions.set(transcriptions?.data);
      }
    }
  }, [organizationId]);
  const selectedTranscription = useStore($transcription);

  const handleDelete = async (transcriptionId: string) => {
    try {
      // Call the backend delete API
      // TODO: Implement this API in C# backend code
      await TranscriptionsService.deleteTranscription(organizationId, transcriptionId);

      // Filter out the deleted item from the local state
      const updatedTranscriptions = transcriptions.data?.filter(
        (ts) => ts.id !== transcriptionId
      );

      // Update the query cache
      queryClient.setQueryData(['getTranscriptions', organizationId], updatedTranscriptions);

      // Update the local storage
      const savedTranscriptionOrders = JSON.parse(
        localStorage.getItem('transcriptions_orders') || '{}',
      );

      if (savedTranscriptionOrders[organizationId]) {
        savedTranscriptionOrders[organizationId] = savedTranscriptionOrders[organizationId].filter(
          (id: string) => id !== transcriptionId,
        );
        localStorage.setItem(
          'transcriptions_orders',
          JSON.stringify(savedTranscriptionOrders),
        );
      }
    } catch (error) {
      console.error("Failed to delete knowledge base:", error);
    }
  };

  const handleDownload = async (transcriptionId: string, format: 'json' | 'csv') => {
    try {
      let downloadUrl: string;
      if (format === 'json') {
        const response: DownloadUrlResponse = await TranscriptionsService.downloadTranscriptionJson(organizationId, transcriptionId);
        downloadUrl = response.downloadUrl;
      } else if (format === 'csv') {
        const response: DownloadUrlResponse = await TranscriptionsService.downloadTranscriptionCsv(organizationId, transcriptionId);
        downloadUrl = response.downloadUrl;
      } else {
        throw new Error("Invalid download format");
      }
      // Access and sanitize the organization name
      const orgNameRaw = org?.name || 'Organization';
      const sanitizedOrgName = sanitizeName(orgNameRaw);
      const shortId = transcriptionId.slice(0, 3);
      const filename = `${sanitizedOrgName}_${shortId}.${format}`;
      // Create a temporary link to trigger the download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Transcription downloaded as ${format.toUpperCase()}.`);
    } catch (error: any) {
      console.error(`Failed to download transcription (${format}):`, error);
      toast.error(`Failed to download transcription as ${format.toUpperCase()}.`);
    }
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const updatedTranscriptions = (items: Transcription[]) => {
        const oldIndex = items.findIndex(
          item => item.id === active.id,
        );
        const newIndex = items.findIndex(item => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      };

      // Update the workspace order in the local query cache
      queryClient.setQueryData(
        ['getTranscriptions', organizationId],
        updatedTranscriptions,
      );
      const newTranscriptions = updatedTranscriptions(transcriptions.data || []);

      // update on the local storage
      const savedTranscriptionOrders = JSON.parse(
        localStorage.getItem('transcriptions_orders') || '{}',
      );

      if (Object.keys(savedTranscriptionOrders)?.includes(organizationId)) {
        savedTranscriptionOrders[organizationId] = newTranscriptions?.map(
          ts => ts.id,
        );
        localStorage.setItem(
          'transcriptions_orders',
          JSON.stringify(savedTranscriptionOrders),
        );
      }
    }
  };

  return (
    <>
      <ScrollArea className="h-[calc(100vh-200px)]">
        <div className="flex flex-col gap-2 pr-4">
          {transcriptions.isLoading && (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}

          {transcriptions.data?.length === 0 && !transcriptions.isLoading && (
            <div className="flex h-32 items-center justify-center">
              <p>No transcriptions found.</p>
            </div>
          )}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={transcriptions.data?.map((ts) => ts.id) || []}
              strategy={verticalListSortingStrategy}
            >
              {sortTranscriptionsUsingSavedOrders(transcriptions?.data || [])?.map(
                (ts) => (
                  <SortableItem key={ts.id} id={ts.id}>
                    <Card
                      className={cn(
                        'w-full',
                        selectedTranscription?.id === ts.id &&
                        'ring-primary-muted ring-1 ring-inset'
                      )}
                    >
                      <CardHeader className="space-y-2">
                        {/* Top Section: Audio File Name and Action Buttons */}
                        <div className="flex items-start justify-between">
                          {/* Audio File Name with Tooltip */}
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <CardTitle className="truncate">
                                  {ts.audioFileName.slice(0, 25)}
                                </CardTitle>
                              </TooltipTrigger>
                              <TooltipContent>
                                {ts.audioFileName}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>

                          {/* Action Buttons */}
                          <div className="flex items-center space-x-2">
                            {/* Download Dropdown */}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="flex items-center gap-1"
                                  aria-label={`Download transcription ${ts.audioFileName}`}
                                >
                                  <Download className="w-4 h-4" />
                                  Download
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent>
                                <DropdownMenuItem
                                  onSelect={() => handleDownload(ts.id, 'json')}
                                >
                                  <Download className="mr-2 h-4 w-4" />
                                  JSON
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onSelect={() => handleDownload(ts.id, 'csv')}
                                >
                                  <Download className="mr-2 h-4 w-4" />
                                  CSV
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>

                            {/* Delete Button */}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 flex items-center gap-1"
                              onClick={() => handleDelete(ts.id)}
                              aria-label={`Delete transcription ${ts.audioFileName}`}
                            >
                              <Trash className="w-4 h-4" />
                              Delete
                            </Button>
                          </div>
                        </div>

                        {/* Transcription Text */}
                        <CardContent className="h-auto overflow-hidden break-words">
                          {ts.text ? (
                            ts.text.split('\n').map((line, index) => (
                              <span key={index}>
                                {line}
                                <br />
                              </span>
                            ))
                          ) : (
                            'Transcription in progress ...'
                          )}
                        </CardContent>

                        {/* Date Information */}
                        <CardDescription>
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center">
                              <Calendar className="mr-1 h-3 w-3" />
                              Created on {new Date(ts.createdAtUtc).toLocaleDateString()}
                            </div>
                            {ts.updatedAtUtc && (
                              <div className="flex items-center">
                                <Calendar className="mr-1 h-3 w-3" />
                                Updated on {new Date(ts.updatedAtUtc).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        </CardDescription>
                      </CardHeader>
                    </Card>
                  </SortableItem>
                )
              )}
            </SortableContext>
          </DndContext>
        </div>
      </ScrollArea>

    </>
  );
};
